import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { loginWithPassword, logoutTenantDashboard } from "@/api/authApi";
import { TotpAuthClient } from "@/api/totpAuthClient";

const totpAuthClient = new TotpAuthClient();
import {
  clearStoredSession,
  readStoredSession,
  writeStoredSession,
  hasValidLoginGrants,
} from "@/lib/authSession";

const AuthContext = createContext(null);

function readSession() {
  const parsed = readStoredSession();
  if (!parsed?.email || !hasValidLoginGrants(parsed)) return null;
  return {
    token: parsed.token,
    email: parsed.email,
    tenant_id: parsed.tenant_id,
    realm: parsed.realm,
    roles: parsed.roles,
    workspace_client_allowlist: parsed.workspace_client_allowlist,
    default_workspace_id: parsed.default_workspace_id ?? "",
  };
}

function writeSession({
  token,
  email,
  rememberMe,
  tenant_id,
  realm,
  roles,
  workspace_client_allowlist,
  default_workspace_id = "",
}) {
  writeStoredSession({
    token,
    email,
    rememberMe,
    tenant_id,
    realm,
    roles,
    workspace_client_allowlist,
    default_workspace_id,
  });
}

function clearSession() {
  clearStoredSession();
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    try {
      const parsed = readStoredSession();
      if (parsed?.token && !hasValidLoginGrants(parsed)) {
        clearStoredSession();
        setUser(null);
        setIsAuthenticated(false);
      } else {
        const session = readSession();
        if (session) {
          setUser({
            email: session.email,
            tenant_id: session.tenant_id,
            realm: session.realm,
            roles: session.roles,
            workspace_client_allowlist: session.workspace_client_allowlist,
            default_workspace_id: session.default_workspace_id ?? "",
          });
          setIsAuthenticated(true);
        }
      }
    } catch {
      clearStoredSession();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsInitializing(false);
    }
  }, []);

  const applyGrants = useCallback((result, rememberMe) => {
    const grants = {
      token: result.token,
      email: result.email,
      tenant_id: result.tenant_id,
      realm: result.realm,
      roles: result.roles,
      workspace_client_allowlist: result.workspace_client_allowlist,
      default_workspace_id: result.default_workspace_id ?? "",
    };
    if (!hasValidLoginGrants(grants)) {
      throw new Error(
        "No tenant or permissions assigned for this account. Contact your administrator."
      );
    }
    writeSession({ ...grants, rememberMe });
    setUser({
      email: result.email,
      tenant_id: result.tenant_id,
      realm: result.realm,
      roles: result.roles,
      workspace_client_allowlist: result.workspace_client_allowlist,
      default_workspace_id: result.default_workspace_id ?? "",
    });
    setIsAuthenticated(true);
  }, []);

  const login = useCallback(
    async ({ email, password, rememberMe }) => {
      const result = await loginWithPassword({
        email,
        password,
        rememberMe,
      });
      if (result.status === "totp_required") {
        return result;
      }
      applyGrants(result, rememberMe);
      return { status: "ok" };
    },
    [applyGrants]
  );

  const verifyTotpLogin = useCallback(
    async ({ totp_session, code, rememberMe }) => {
      const result = await totpAuthClient.verify({ totp_session, code });
      applyGrants(result, rememberMe);
      return { status: "ok" };
    },
    [applyGrants]
  );

  const signOut = useCallback(async () => {
    const parsed = readStoredSession();
    const tok = parsed?.token;
    if (tok) {
      try {
        await logoutTenantDashboard(tok);
      } catch {
        // Still clear local session if revoke fails (token may already be invalid).
      }
    }
    clearSession();
    setUser(null);
    setIsAuthenticated(false);
    navigate("/login", { replace: true });
  }, [navigate]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isInitializing,
      login,
      verifyTotpLogin,
      applyGrants,
      signOut,
    }),
    [user, isAuthenticated, isInitializing, login, verifyTotpLogin, applyGrants, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook lives next to provider; fast-refresh wants split files but this pattern is standard.
// eslint-disable-next-line react-refresh/only-export-components -- useAuthContext pairs with AuthProvider
export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return ctx;
}
